<template>
  <main>
    <ValidationObserver
      ref="form"
      v-slot="{ invalid, dirty }"
    >
      <div
        class="unit__form"
        data-cy="unit-form__container"
      >
        <!-- Submit -->
        <HeaderMenu
          :enable-custom-back-action="true"
          @custom-back-action="handleBackAction(dirty)"
        >
          <div class="flex gap-4">
            <BaseButton
              type="submit"
              class="bg-green-700 hover:bg-green-600 font-lato text-sm text-white"
              data-cy="unit-form__save-button"
              :disabled="invalid || logo.source === ''"
              @click="onConfirmation"
            >
              <p>
                Simpan Data
              </p>
            </BaseButton>
          </div>
        </HeaderMenu>

        <div class="w-full h-full p-8 rounded-xl bg-white">
          <h2 class="font-bold text-[15px] text-blue-gray-900 font-lato mb-4">
            Upload Logo
          </h2>
          <!-- Logo Upload -->
          <section class="mb-4">
            <div class="flex gap-[30px]">
              <div
                v-if="logo.source"
                class="flex-shrink-0 w-[124px] h-[124px] overflow-hidden bg-gray-50 border border-dashed border-gray-400 rounded-md "
              >
                <img
                  width="58"
                  height="58"
                  class="w-full h-full object-contain object-center"
                  :src="logo.source"
                  alt="Ilustrasi logo"
                >
              </div>
              <div
                v-else
                class="flex items-center justify-center w-[124px] h-[124px] bg-gray-50 border border-dashed border-gray-400 rounded-md"
              >
                <img
                  src="@/assets/icons/image-placeholder.svg"
                  alt="Gambar placeholder"
                  width="34"
                  height="34"
                >
              </div>

              <div class="flex flex-col">
                <h2 class="font-medium text-blue-gray-900 font-roboto text-sm">
                  Logo Perangkat Daerah
                </h2>
                <p class="w-full text-gray-800 text-sm mt-1">
                  Ukuran maksimal 2 MB, dengan resolusi 200 x 200 pixel.
                </p>
                <p class="w-full text-gray-800 text-sm mt-1">
                  File yang didukung adalah .jpg dan .png
                </p>
                <div
                  v-if="!logo.source"
                  class="flex items-center gap-4"
                >
                  <BaseButton
                    type="button"
                    class="text-white bg-green-700 text-sm my-4"
                    data-cy="unit__button-upload-logo"
                    @click="$refs.logo.click()"
                  >
                    Pilih File
                    <template #icon-left>
                      <JdsIcon
                        name="plus"
                        size="14px"
                        fill="#ffffff"
                      />
                    </template>
                  </BaseButton>
                  <span
                    class="text-sm font-normal leading-6 text-gray-800 font-lato"
                    data-cy="unit__text-empty-logo"
                  >
                    Belum ada file terpilih.
                  </span>
                </div>
                <div v-else>
                  <BaseButton
                    type="button"
                    class="bg-red-600 text-white font-lato text-sm my-4"
                    @click="handleDeleteLogo"
                  >
                    <template #icon-left>
                      <JdsIcon
                        name="trash"
                        size="16px"
                      />
                    </template>
                    <p>
                      Hapus
                    </p>
                  </BaseButton>
                </div>

                <ValidationProvider
                  v-slot="{ errors }"
                  ref="logoWrapper"
                  rules="required|ext:png,jpg,jpeg|size:2000|maxdimensions:200,200"
                >
                  <input
                    id="logoInput"
                    ref="logo"
                    type="file"
                    hidden="true"
                    accept="image/png, image/jpg, image/jpeg"
                    @change="onLogoChange"
                  >
                  <p
                    class="text-[#D32F2F] text-[13px] leading-[23px]"
                    data-cy="unit__text-error-logo"
                  >
                    {{ errors[0] }}
                  </p>
                </ValidationProvider>
              </div>
            </div>
          </section>

          <section>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required|max:150"
            >
              <div class="flex flex-col mb-5">
                <label
                  class="font-lato text-blue-gray-800 mb-3 text-[15px]"
                >
                  Nama Perangkat Daerah
                </label>
                <JdsInputText
                  v-model.trim="form.name"
                  placeholder="Masukkan nama perangkat daerah"
                  data-cy="unit-form__name"
                  :error-message="errors[0]"
                />
              </div>
            </ValidationProvider>

            <ValidationProvider
              v-slot="{ errors }"
              rules="required|max:150"
            >
              <div class="flex flex-col mb-5">
                <label
                  class="font-lato text-blue-gray-800 mb-3 text-[15px]"
                >
                  Nama Pimpinan
                </label>
                <JdsInputText
                  v-model.trim="form.chief"
                  placeholder="Masukkan nama pimpinan"
                  data-cy="unit-form__chief"
                  :error-message="errors[0]"
                />
              </div>
            </ValidationProvider>

            <ValidationProvider
              v-slot="{ errors }"
              rules="required|phonenumber"
            >
              <div class="flex flex-col mb-5">
                <label
                  class="font-lato text-blue-gray-800 mb-3 text-[15px]"
                >
                  No. Telepon
                </label>
                <JdsInputText
                  v-model.trim="form.phone"
                  placeholder="Masukkan no telepon"
                  data-cy="unit-form__phone"
                  :error-message="errors[0]"
                />
              </div>
            </ValidationProvider>

            <ValidationProvider
              v-slot="{ errors }"
              rules="required|url|max:200"
            >
              <div class="flex flex-col mb-5">
                <label
                  class="font-lato text-blue-gray-800 mb-3 text-[15px]"
                >
                  Link Website
                </label>
                <JdsInputText
                  v-model="form.website"
                  placeholder="contoh: https://"
                  :error-message="errors[0]"
                  data-cy="unit-form__website"
                />
              </div>
            </ValidationProvider>

            <ValidationProvider
              v-slot="{ errors }"
              rules="url|max:200"
            >
              <div class="flex flex-col mb-5">
                <label
                  class="font-lato text-blue-gray-800 mb-3 text-[15px]"
                >
                  Link PPID <span class="text-gray-500">(Opsional)</span>
                </label>
                <JdsInputText
                  v-model="form.ppid"
                  placeholder="contoh: https://"
                  :error-message="errors[0]"
                  data-cy="unit-form__ppid"
                />
              </div>
            </ValidationProvider>

            <ValidationProvider
              v-slot="{ errors }"
              rules="required|max:200"
            >
              <div class="flex flex-col mb-5">
                <label
                  class="font-lato text-blue-gray-800 mb-3 text-[15px]"
                >
                  Alamat
                </label>
                <textarea
                  v-model="form.address"
                  placeholder="Masukkan alamat perangkat daerah"
                  rows="3"
                  maxlength="200"
                  class="w-full border border-gray-500 rounded-lg px-2 py-1 bg-gray-50 mb-1 hover:bg-white hover:border-green-600 focus:outline-none focus:border-green-500 focus:outline-1 focus:outline-offset-[-2px] focus:outline-yellow-500"
                  data-cy="unit-form__address"
                />
                <span class="font-lato text-[13px] text-red-600 mt-1">
                  {{ errors[0] }}
                </span>
              </div>
            </ValidationProvider>
          </section>
        </div>
      </div>
    </ValidationObserver>

    <!-- Confirmation Popup -->
    <BaseModal
      :open="submitStatus === 'CONFIRMATION' || submitStatus === 'DELETE'"
      data-cy="unit-form__confirmation-modal"
    >
      <div class="w-full h-full px-2 pb-4">
        <h1 class="font-roboto font-medium text-green-700 text-[21px] leading-[34px] mb-6">
          {{ modalMessage.title }}
        </h1>
        <div class="flex items-center gap-4">
          <p class="text-sm leading-6 to-blue-gray-800">
            {{ modalMessage.message }}
          </p>
        </div>
      </div>
      <template #footer>
        <div class="flex w-full h-full items-center justify-end gap-4 p-2">
          <BaseButton
            class="border border-green-700 hover:bg-green-50 text-sm text-green-700"
            data-cy="unit-form__confirmation-cancel"
            @click="onCancel"
          >
            {{ submitStatus === 'CONFIRMATION' ? 'Tidak' : 'Batalkan' }}
          </BaseButton>
          <BaseButton
            v-if="submitStatus === 'CONFIRMATION'"
            class="bg-green-700 hover:bg-green-600 text-sm text-white"
            data-cy="unit-form__confirmation-save"
            @click="handleSubmit"
          >
            Ya, simpan data
          </BaseButton>
          <BaseButton
            v-if="submitStatus === 'DELETE'"
            class="bg-green-700 hover:bg-green-600 text-sm text-white"
            data-cy="unit-form__confirmation-delete"
            @click="$router.back()"
          >
            Ya, yakin
          </BaseButton>
        </div>
      </template>
    </BaseModal>

    <!-- Submit Progress -->
    <ProgressModal
      :open="submitStatus === 'LOADING'"
      :value="submitProgress"
      title="Menyimpan Data"
      message="Mohon tunggu, penyimpanan data sedang diproses"
      data-cy="unit-form__progress-modal"
    />

    <!-- Success/Error Message -->
    <BaseModal
      :open="submitStatus !== 'NONE' && submitStatus !== 'LOADING'"
      data-cy="unit-form__message-modal"
    >
      <div class="w-full h-full px-2 pb-4">
        <h1 class="font-roboto font-medium text-green-700 text-[21px] leading-[34px] mb-6">
          {{ modalMessage.title }}
        </h1>
        <div class="flex items-center gap-4">
          <JdsIcon
            v-if="submitStatus === 'SUCCESS'"
            name="check-mark-circle"
            class="text-green-600"
          />
          <JdsIcon
            v-else-if="submitStatus === 'ERROR'"
            name="warning"
            class="text-red-600"
          />
          <p class="text-sm leading-6 to-blue-gray-800">
            {{ modalMessage.message }}
          </p>
        </div>
      </div>
      <template #footer>
        <div class="flex w-full h-full items-center justify-center gap-4 p-2">
          <BaseButton
            class="bg-green-700 hover:bg-green-600 text-sm text-white"
            data-cy="unit-form__message-button"
            @click="messageAction"
          >
            Saya Mengerti
          </BaseButton>
        </div>
      </template>
    </BaseModal>
  </main>
</template>

<script>
import HeaderMenu from '@/common/components/HeaderMenu';
import BaseButton from '@/common/components/BaseButton';
import BaseModal from '@/common/components/BaseModal';
import ProgressModal from '@/common/components/ProgressModal';

import '@/common/helpers/vee-validate.js';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { delay } from '@/common/helpers/delay';
import { EventBus } from '@/common/helpers/event-bus';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';

const mediaRepository = RepositoryFactory.get('media');
const unitRepository = RepositoryFactory.get('unit');

const FORM_SUBMIT_STATUS = Object.freeze({
  NONE: 'NONE',
  CONFIRMATION: 'CONFIRMATION',
  LOADING: 'LOADING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  DELETE: 'DELETE',
});

export default {
  name: 'CreateEditUnit',
  components: {
    HeaderMenu,
    BaseButton,
    BaseModal,
    ProgressModal,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      form: {
        logo: '',
        name: '',
        chief: '',
        phone: '',
        website: '',
        ppid: '',
        address: '',
      },
      logo: {
        name: '',
        source: '',
        file: null,
      },
      originalLogoUrl: '',
      modalMessage: {
        title: '',
        message: '',
        type: '',
      },
      submitStatus: FORM_SUBMIT_STATUS.NONE,
      submitProgress: 0,
      showExitConfirmation: false,
    };
  },
  computed: {
    mode() {
      return this.$route.meta?.mode || 'create';
    },
    isEditMode() {
      return this.mode === 'edit';
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.setInitialData();
    }
  },
  methods: {
    async setInitialData() {
      try {
        const { id } = this.$route.params;
        const response = await unitRepository.getUnitById(id);

        if (!response || !response.data) { // check if response and data are valid
          throw new Error('Respons API tidak valid atau kosong.');
        }

        const { data } = response.data;
        this.form = {
          logo: data.logo ?? '',
          name: data.name ?? '',
          chief: data.chief ?? '',
          phone: data.phone ?? '',
          website: data.website ?? '',
          ppid: data.ppid ?? '',
          address: data.address ?? '',
        };
        this.logo.source = data.logo ?? '';
        this.originalLogoUrl = data.logo ?? '';
      } catch (error) {
        if (error.response?.status === 403) {
          EventBus.$emit('error:forbidden');
        }
      }
    },
    async onLogoChange(event) {
      const [file] = event.target.files;

      if (!file) {
        return;
      }

      const isValid = await this.$refs.logoWrapper.validate(file);
      if (!isValid.valid) {
        if (this.$refs.logo) {
          this.$refs.logo.value = '';
        }
        this.logo.file = null;
        return;
      }

      this.logo.source = URL.createObjectURL(file);
      this.logo.name = file.name;
      this.logo.file = file;
    },
    handleDeleteLogo() {
      this.$refs.logoWrapper.reset();
      this.logo.source = '';
      this.logo.name = '';
      this.logo.file = null;
      if (this.$refs.logo) {
        this.$refs.logo.value = '';
      }
      this.form.logo = '';
    },
    onConfirmation() {
      this.submitStatus = FORM_SUBMIT_STATUS.CONFIRMATION;
      this.modalMessage.title = 'Simpan Data';
      this.modalMessage.message = 'Apakah Anda ingin menyimpan data?';
    },
    onCancel() {
      this.submitStatus = FORM_SUBMIT_STATUS.NONE;
    },
    messageAction() {
      if (this.submitStatus === FORM_SUBMIT_STATUS.SUCCESS) {
        this.$router.push('/profil-jawa-barat/tentang/perangkat-daerah');
      } else {
        this.resetSubmitState();
      }
    },
    handleBackAction(dirty) {
      const hasLogoChangedInEditMode = this.isEditMode && this.originalLogoUrl !== '' && this.form.logo !== this.originalLogoUrl;
      if (dirty || this.logo.file !== null || hasLogoChangedInEditMode) {
        this.submitStatus = FORM_SUBMIT_STATUS.DELETE;
        this.modalMessage.title = 'Yakin ingin kembali?';
        this.modalMessage.message = 'Data yang sudah kamu isi akan hilang.';
      } else {
        this.$router.back();
      }
    },
    resetSubmitState() {
      this.submitStatus = FORM_SUBMIT_STATUS.NONE;
      this.submitProgress = 0;
      this.modalMessage.title = '';
      this.modalMessage.message = '';
    },
    async handleSubmit() {
      this.submitStatus = FORM_SUBMIT_STATUS.LOADING;
      this.submitProgress = 20;

      try {
        if (this.logo.file) {
          await this.uploadImage(this.logo.file);
        }
        this.submitProgress = 50;

        if (this.isEditMode && this.originalLogoUrl && this.form.logo !== this.originalLogoUrl) {
          try {
            await this.deleteUploadedImage(this.originalLogoUrl);
          } catch (deleteError) {
            // eslint-disable-next-line no-console
            console.error('Failed to delete old logo:', deleteError);
          }
        }

        await this.saveFormData();
      } catch (error) {
        this.submitProgress = 100;
        // eslint-disable-next-line no-console
        console.error('[ERROR - UNIT FORM] General submission error:', error);
        const { response } = error;

        if (response?.status === 400) {
          this.modalMessage.title = 'Sudah terdaftar!';
          this.modalMessage.message = response.data.message || 'Nama Perangkat Daerah ini sudah ada di sistem. Coba masukkan data yang berbeda.';
        } else {
          this.modalMessage.title = 'Gagal!';
          this.modalMessage.message = 'Terjadi kesalahan saat menyimpan data. Silakan coba lagi.';
        }
        this.submitStatus = FORM_SUBMIT_STATUS.ERROR;
      }
    },
    async saveFormData() {
      this.submitProgress = 75;
      await delay(100);

      if (this.mode === 'create') {
        await unitRepository.createUnit(this.form);
      } else {
        const { id } = this.$route.params;
        await unitRepository.updateUnit(this.form, id);
      }

      this.submitProgress = 100;
      await delay(100);

      this.modalMessage.title = 'Berhasil!';
      this.modalMessage.message = 'Data berhasil disimpan.';
      this.submitStatus = FORM_SUBMIT_STATUS.SUCCESS;
    },
    async uploadImage(file) {
      if (!file) {
        return;
      }

      const formData = new FormData();
      formData.append('file', file, file.name);

      try {
        const response = await mediaRepository.uploadMedia(formData, { domain: 'unit-logo' });
        const { data, status } = response;

        if (status === 201) {
          this.form.logo = data.file_download_uri;
          this.logo.source = data.file_download_uri;
          this.logo.name = data.file_name;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[ERROR - UNIT FORM] Failed to upload image. ', error);
        this.handleDeleteLogo();
        this.modalMessage.title = 'Gagal!';
        this.modalMessage.message = 'Gagal mengunggah logo. Silakan coba lagi.';
        this.submitStatus = FORM_SUBMIT_STATUS.ERROR;
        throw error;
      }
    },
    async deleteUploadedImage(logoUrl) {
      try {
        const response = await mediaRepository.deleteMedia({
          key: logoUrl,
          domain: 'unit-logo',
        });
        return Promise.resolve(response);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting media:', error);
        throw new Error(`Failed to delete media: ${error.message}`);
      }
    },
  },
};
</script>
